import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { UseQueryOptions, UseQueryResult } from 'react-query';
import type { PriceImportRow, PriceImportRowStatus } from './types';
import { priceImportsEndpointIdentifier } from './use-get-price-import-runs-api';

/**
 * A key NAMESPACE, not a URL — the rows live under the run's path. Separate
 * from the run key so patching a row does not blow away the run summary cache,
 * and so a row edit can prefix-invalidate every bucket at once (an edit can
 * move a row between buckets).
 */
export const priceImportRowsEndpointIdentifier = 'ums/v1/cms/supplier-price-imports/rows';

interface ResponseData {
  data: PriceImportRow[];
}

type TData = ResponseData;
type TError = AxiosError;
type TQueryKey = [string, number | undefined, { status?: PriceImportRowStatus }];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetPriceImportRowsApi(
  runId: number | undefined,
  status?: PriceImportRowStatus,
  options?: Options
): ReturnType {
  const toast = useCustomToast();
  const isLoggedIn = useIsLoggedIn();

  const queryFn = async () => {
    try {
      const { data } = await http.get(`${priceImportsEndpointIdentifier}/${runId}/rows`, {
        params: status ? { status } : undefined,
      });
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({ description: error.response.data.message, status: 'error' });

      throw error;
    }
  };

  return useQueryWrapper([priceImportRowsEndpointIdentifier, runId, { status }], queryFn, {
    enabled: isLoggedIn && !!runId,
    ...options,
  });
}
